/**
 * 주문 완료 화면 — FCM 웹푸시 권한 안내 배너
 *
 * @CODE:CUSTOMER-WEBPUSH | SPEC: docs/superpowers/specs/2026-06-11-customer-realtime-fix-and-webpush-design.md
 */
import { useEffect, useRef, useState } from 'react';
import {
  isPushSupported,
  shouldShowPushOptIn,
  getDismissedAt,
  enablePush,
  enableGuestPush,
  registerGuestPushIfGranted,
  dismissPushOptIn,
  type GuestPushContext,
} from '@/lib/push';
import { useAuth } from '@/contexts/AuthContext';

export interface PushOptInBannerLabels {
  title: string;
  accept: string;
  later: string;
  /** #694 게스트 전용 제목 — 비로그인 + guestContext 시 title 대체 (미지정 시 title 폴백) */
  guestTitle?: string;
  /** #694 게스트 보조 줄 — 로그인 권장 안내 */
  loginHint?: string;
}

interface PushOptInBannerProps {
  labels: PushOptInBannerLabels;
  /**
   * #694: 비회원 푸시 귀속 컨텍스트 — 비로그인 + 존재 시 게스트 분기 활성.
   * - permission==='default' → 게스트 전용 문구 배너 → 수락 시 enableGuestPush
   * - permission==='granted' → 배너 없이 자동 재귀속 (결정 3, fire-and-forget)
   * 비로그인 + 미존재(컨텍스트 불명)면 기존처럼 숨김.
   */
  guestContext?: GuestPushContext;
  /**
   * #680 리뷰: 배너 노출/소멸 통지 — 실제 노출 시 true, 수락/나중에 처리 완료·언마운트 시 false.
   * OrderSuccessScreen 이 자동 이동 카운트다운 일시정지에 사용한다.
   */
  onInteractionChange?: (interacting: boolean) => void;
}

export function PushOptInBanner({ labels, guestContext, onInteractionChange }: PushOptInBannerProps) {
  const { isLoggedIn, isCheckingSession } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  // #694: 로그인 = 계정 경로(기존) / 비로그인 + guestContext = 게스트 경로.
  // 세션 복원 중(isCheckingSession)에는 게스트 판정을 보류 — 3DS 복귀 같은 풀 리로드
  // 직후 isLoggedIn 이 일시적으로 false 라, 로그인 사용자에게 게스트 배너/자동 등록이
  // 선발화하는 오분류를 차단한다 (codex 4차 P2). 복원 완료 후 effect 가 재평가한다.
  const isGuestMode = !isCheckingSession && !isLoggedIn && guestContext !== undefined;
  const shown = (isLoggedIn || isGuestMode) && visible;

  // 최신 콜백을 ref 로 보관 — 콜백 identity 변경(인라인 함수)으로 인한 false→true 재통지 방지.
  const onInteractionChangeRef = useRef(onInteractionChange);
  useEffect(() => {
    onInteractionChangeRef.current = onInteractionChange;
  });

  // 노출 시 true, 숨김/언마운트 시 false 통지 (실제로 visible 일 때만 true).
  useEffect(() => {
    if (!shown) return undefined;
    onInteractionChangeRef.current?.(true);
    return () => {
      onInteractionChangeRef.current?.(false);
    };
  }, [shown]);

  useEffect(() => {
    if (!isLoggedIn && !isGuestMode) return;

    let cancelled = false;
    isPushSupported().then((supported) => {
      if (cancelled) return;
      const permission =
        typeof Notification !== 'undefined'
          ? (Notification.permission as NotificationPermission)
          : 'denied';

      // #694 결정 3: 권한이 이미 granted 인 비회원의 새 주문/세션은 배너 없이 자동 재귀속.
      // (로그인 고객의 granted 재등록은 앱 부팅 refreshPushToken 이 담당 — 여기선 게스트만.)
      if (isGuestMode && guestContext && supported && permission === 'granted') {
        void registerGuestPushIfGranted(guestContext);
        return;
      }

      const show = shouldShowPushOptIn({
        supported,
        permission,
        dismissedAt: getDismissedAt(),
      });
      setVisible(show);
    });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, isGuestMode, guestContext]);

  if (!shown) return null;

  const handleAccept = async () => {
    setBusy(true);
    try {
      const enabled = isGuestMode && guestContext
        ? await enableGuestPush(guestContext)
        : await enablePush();
      if (enabled) {
        setVisible(false);
        return;
      }
      // 권한 거부 → enablePush/enableGuestPush 가 dismiss 를 기록했고 permission 도 default 가 아니므로 숨김.
      // 권한은 허용됐지만 토큰 발급/등록이 실패한 경우 → 배너를 유지해 재시도를 허용한다
      // (숨기면 granted 상태라 같은 세션에서 다시 노출되지 않아 푸시가 조용히 꺼짐 — codex 6차 P2).
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        setVisible(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleLater = () => {
    dismissPushOptIn();
    setVisible(false);
  };

  const title = isGuestMode ? (labels.guestTitle ?? labels.title) : labels.title;

  return (
    <div
      style={{
        marginTop: '16px',
        padding: '14px 16px',
        borderRadius: '12px',
        background: 'rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{title}</p>
      {isGuestMode && labels.loginHint && (
        <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>{labels.loginHint}</p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          disabled={busy}
          onClick={handleAccept}
          style={{
            flex: 1,
            padding: '8px 0',
            borderRadius: '8px',
            border: 'none',
            background: '#111',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {labels.accept}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={handleLater}
          style={{
            flex: 1,
            padding: '8px 0',
            borderRadius: '8px',
            border: '1px solid #ccc',
            background: 'transparent',
            fontSize: '13px',
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {labels.later}
        </button>
      </div>
    </div>
  );
}
